
import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { of, map as rxMap } from 'rxjs';
import { DoubleSelectDto, HTMLDto, ManufacturerDto, MceApiService, StringSelectDto } from '../../../core/mceapi.client.generated';
import { apiOptionDtoToOption } from '../../../core/mceapi.client.helpers';
import { FormDirective } from '../../../shared/directives/form.directive';
import { Option, isSome, match, match2, none, some } from '../../../shared/functions/functional';
@Component({
    selector: 'app-equipment-data-cables',
    imports: [FormDirective, ReactiveFormsModule, FormsModule, MatProgressSpinnerModule, MatSelectModule],
    templateUrl: './equipment-data-cables.component.html',
    styleUrl: './equipment-data-cables.component.scss'
})
export class EquipmentDataCablesComponent {

  private mceApiService = inject(MceApiService);

  protected readonly formValid = signal<boolean>(false);
  protected readonly formDirty = signal<boolean>(false);

  // Manufactures
  manufacturersResource = rxResource({
    stream: () => this.mceApiService.v1CableLibraryManufacturers()
  });
  manufacturers = computed(() => this.manufacturersResource.value() ?? []);

  // Manufacturers equipment ranges/sources
  rangesResource = rxResource({
    params: () => this.manufacturerUid(),
    stream: ({ params }) =>
      match(
        () => of([] as StringSelectDto[]),
        (x: string) =>
          this.mceApiService.v1CableLibraryManufacturersSources(x)
      )(params)
  });
  ranges = computed(() => this.rangesResource.value() ?? []);

  // Cables for selected range
  cablesResource = rxResource({
    params: () => this.rangeUid(),
    stream: ({ params }) =>
      match2(
        () => of([] as StringSelectDto[]),
        (manufacturerUid:string, rangeUid:string) =>
          this.mceApiService.v1CableLibraryManufacturersLibrary(manufacturerUid, rangeUid)
      )(this.manufacturerUid(),params)
  });
  cables = computed(() => this.cablesResource.value() ?? []);

  /// Get CSA for selected cable
  csasResource = rxResource({
    params: () => this.libraryUid(),
    stream: ({ params }) =>
      match2(
        () => of([] as DoubleSelectDto[]),
        (manufacturerUid: string, libraryUid: string) =>
          this.mceApiService.v1CableLibraryManufacturersCsa(manufacturerUid, libraryUid)
      )(this.manufacturerUid(), params)
  });
  csas = computed(() => this.csasResource.value() ?? []);

  // Get datasheet for selected cable and selected CSA
  datasheetResource = rxResource({
    params: () => this.csa(),
    stream: ({ params }) =>
      match2(
        () => of(none as Option<HTMLDto>),
        (libraryUid: string, csa:number) =>
          this.mceApiService.v1CableLibraryDatasheetHtml(libraryUid, csa).pipe(
            rxMap(x => apiOptionDtoToOption(x) as Option<HTMLDto>))
      )(this.libraryUid(), params)
  });
  datasheet = computed(() => this.datasheetResource.value() ?? none);

  manufacturerUid = signal<Option<string>>(none);
  rangeUid = signal<Option<string>>(none);
  libraryUid = signal<Option<string>>(none);
  csa = signal<Option<number>>(none);

  protected readonly viewModel = computed(() => {
    return {

      formValid: this.formValid(),
      formDirty: this.formDirty(),

      manufacturers: this.manufacturers().map((x:ManufacturerDto) => ({
          value: x.guid,
          label: x.name
        })),

      ranges: this.ranges(),
      showRanges: this.ranges().length > 0,

      cables: this.cables(),
      showCables: this.cables().length > 0,

      csas: this.csas(),
      showCSAs: this.csas().length > 0,

      datasheet: match(() => '', (x:HTMLDto) => x.html)(this.datasheet()),
      showDatasheet: isSome(this.datasheet()),

      showSpinner:
        this.manufacturersResource.isLoading() ||
        this.rangesResource.isLoading() ||
        this.cablesResource.isLoading() ||
        this.csasResource.isLoading() ||
        this.datasheetResource.isLoading()
    };
  });

  protected get vm() { return this.viewModel(); }

  onManufacturerChange($event: MatSelectChange): void { this.manufacturerUid.set(some($event.value)); }

  onRangeChange($event: MatSelectChange): void { this.rangeUid.set(some($event.value)); }

  onCableChange($event: MatSelectChange): void { this.libraryUid.set(some($event.value)); }

  onCSAChange($event: MatSelectChange): void { this.csa.set(some($event.value)); }

}

